import { gql } from 'graphql-request';

// Fragment for organization subscription fields (NEW SYSTEM)
export const ORGANIZATION_SUBSCRIPTION_FIELDS = gql`
  fragment OrganizationSubscriptionFields on OrganizationSubscriptionDto {
    id
    organizationId
    planTemplateId
    subscriptionType
    status
    billingPeriod
    trialEndsAt
    startedAt
    canceledAt
    nmiSubscriptionId
    includedSeats
    planSnapshot {
      planId
      name
      basePriceCents
      includedSeats
      isAddOn
      purchasedAt
      seatCountingRoles
    }
    createdBy
    updatedBy
    createdAt
    updatedAt
    planName
    organizationName
  }
`;

// Fragment for payment method fields
export const PAYMENT_METHOD_FIELDS = gql`
  fragment PaymentMethodFields on PaymentMethodDto {
    paymentMethodId
    organizationId
    nmiCustomerVaultId
    type
    last4
    brand
    expiryMonth
    expiryYear
    accountHolderName
    accountType
    isDefault
    createdAt
    updatedAt
  }
`;

// Fragment for transaction fields
export const TRANSACTION_FIELDS = gql`
  fragment TransactionFields on SubscriptionTransactionDto {
    transactionId
    subscriptionId
    nmiTransactionId
    amount
    status
    type
    nmiResponseCode
    failureReason
    nmiResponseText
    transactionDate
    createdAt
  }
`;

// Fragment for pricing config fields
export const PRICING_CONFIG_FIELDS = gql`
  fragment PricingConfigFields on PricingConfigDto {
    pricingConfigId
    pricingType
    key
    name
    description
    amount
    billingCycle
    trialDurationDays
    isActive
    displayOrder
    createdAt
    updatedAt
  }
`;

// Fragment for active seat fields
export const ACTIVE_SEAT_FIELDS = gql`
  fragment ActiveSeatFields on ActiveSeatDto {
    userId
    userEmail
    userName
    roleName
    createdAt
  }
`;

// Sprint 17, story-082. Shared by SubscriptionPlanTemplateDto.addOns (public pricing) and
// getLinkedAddOnsForOrganization (the org's own current entitlements) — same shape, deliberately.
export const PLAN_ADDON_SUMMARY_FIELDS = gql`
  fragment PlanAddonSummaryFields on PlanAddonSummaryDto {
    id
    name
    includedSeats
    priceCents
  }
`;

// Get subscription for current organization (NEW SYSTEM)
export const getOrganizationSubscription = gql`
  query getOrganizationSubscription {
    getOrganizationSubscription {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Get subscription analytics (Super Admin)
export const getSubscriptionAnalytics = gql`
  query getSubscriptionAnalytics {
    getSubscriptionAnalytics {
      totalActive
      totalTrial
      totalCanceled
      totalPastDue
      monthlyRecurringRevenue
      annualRecurringRevenue
      trialConversionRate
      newSubscriptionsThisMonth
      canceledThisMonth
      churnRate
    }
  }
`;

// Get active seats for organization
export const getActiveSeats = gql`
  query getActiveSeats($organizationId: String) {
    getActiveSeats(organizationId: $organizationId) {
      ...ActiveSeatFields
    }
  }
  ${ACTIVE_SEAT_FIELDS}
`;

// Get payment methods (optionally for a specific organization)
export const getPaymentMethods = gql`
  query getPaymentMethods($organizationId: String) {
    getPaymentMethods(organizationId: $organizationId) {
      ...PaymentMethodFields
    }
  }
  ${PAYMENT_METHOD_FIELDS}
`;

// Get transaction history for a specific subscription
export const getTransactionHistory = gql`
  query getTransactionHistory($subscriptionId: String!, $page: Float, $limit: Float) {
    getTransactionHistory(subscriptionId: $subscriptionId, page: $page, limit: $limit) {
      ...TransactionFields
    }
  }
  ${TRANSACTION_FIELDS}
`;

// Get transaction history for entire organization (all subscriptions)
export const getOrganizationTransactionHistory = gql`
  query getOrganizationTransactionHistory($organizationId: String, $page: Float, $limit: Float) {
    getOrganizationTransactionHistory(organizationId: $organizationId, page: $page, limit: $limit) {
      ...TransactionFields
    }
  }
  ${TRANSACTION_FIELDS}
`;

// Cancel subscription (NEW SYSTEM)
export const cancelOrganizationSubscription = gql`
  mutation cancelOrganizationSubscription($subscriptionId: String!) {
    cancelOrganizationSubscription(subscriptionId: $subscriptionId) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Pause subscription (NEW SYSTEM)
export const pauseOrganizationSubscription = gql`
  mutation pauseOrganizationSubscription($subscriptionId: String!) {
    pauseOrganizationSubscription(subscriptionId: $subscriptionId) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Resume subscription (NEW SYSTEM)
export const resumeOrganizationSubscription = gql`
  mutation resumeOrganizationSubscription($subscriptionId: String!) {
    resumeOrganizationSubscription(subscriptionId: $subscriptionId) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Add addon to subscription (NEW SYSTEM)
// Creates a new independent addon subscription
export const addAddonSubscription = gql`
  mutation addAddonSubscription($addonId: String!, $billingPeriod: String!) {
    addAddonSubscription(addonId: $addonId, billingPeriod: $billingPeriod) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Remove addon subscription (NEW SYSTEM)
// Cancels an addon subscription by its ID
export const removeAddonSubscription = gql`
  mutation removeAddonSubscription($subscriptionId: String!) {
    removeAddonSubscription(subscriptionId: $subscriptionId) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Reactivate suspended organization subscription
// Used by organization OWNERs to reactivate their suspended organization
export const reactivateOrganizationSubscription = gql`
  mutation reactivateOrganizationSubscription($input: ReactivateSubscriptionInput!) {
    reactivateOrganizationSubscription(input: $input) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Convert a lapsed card-free trial into a paying subscription.
// Distinct from reactivateOrganizationSubscription, which restores an organization
// SUSPENDED after failed charges — a lapsed trial never had a payment fail.
export const activateSubscriptionAfterTrial = gql`
  mutation activateSubscriptionAfterTrial($input: ReactivateSubscriptionInput!) {
    activateSubscriptionAfterTrial(input: $input) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// How many free-trial organizations the current user owns, against the cap.
export const getFreeTrialOrganizationUsage = gql`
  query getFreeTrialOrganizationUsage {
    getFreeTrialOrganizationUsage {
      used
      limit
      canCreateMore
    }
  }
`;

// Get all addon subscriptions for organization (NEW SYSTEM)
export const getAddonSubscriptions = gql`
  query getAddonSubscriptions {
    getAddonSubscriptions {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Get total included seats for organization (NEW SYSTEM)
export const getTotalIncludedSeats = gql`
  query getTotalIncludedSeats {
    getTotalIncludedSeats
  }
`;

// Add payment method
export const addPaymentMethod = gql`
  mutation addPaymentMethod($input: AddPaymentMethodInput!) {
    addPaymentMethod(input: $input) {
      ...PaymentMethodFields
    }
  }
  ${PAYMENT_METHOD_FIELDS}
`;

// Update payment method
export const updatePaymentMethod = gql`
  mutation updatePaymentMethod($paymentMethodId: String!, $input: UpdatePaymentMethodInput!) {
    updatePaymentMethod(paymentMethodId: $paymentMethodId, input: $input) {
      ...PaymentMethodFields
    }
  }
  ${PAYMENT_METHOD_FIELDS}
`;

// Delete payment method
export const deletePaymentMethod = gql`
  mutation deletePaymentMethod($paymentMethodId: String!) {
    deletePaymentMethod(paymentMethodId: $paymentMethodId) {
      deleted
      warning
    }
  }
`;

// Set default payment method
export const setDefaultPaymentMethod = gql`
  mutation setDefaultPaymentMethod($paymentMethodId: String!) {
    setDefaultPaymentMethod(paymentMethodId: $paymentMethodId) {
      ...PaymentMethodFields
    }
  }
  ${PAYMENT_METHOD_FIELDS}
`;

// Get subscription plan templates (NEW SYSTEM)
export const getSubscriptionPlans = gql`
  query getSubscriptionPlans {
    getSubscriptionPlans {
      id
      name
      basePriceCents
      includedSeats
      isPublic
      isAddOn
      seatCountingRoles
      trialDurationDays
      requiresPaymentUpfront
      validFrom
      validTo
      createdAt
      updatedAt
      addOns {
        ...PlanAddonSummaryFields
      }
    }
  }
  ${PLAN_ADDON_SUMMARY_FIELDS}
`;

// Sprint 17, story-082 (added run 2). Org-scoped view of the caller's own current base plan's
// linked add-ons — used by the Add Seats flow instead of the public getSubscriptionPlans, since
// an org on a private/since-unpublished plan still has real, purchasable links here.
export const getLinkedAddOnsForOrganization = gql`
  query getLinkedAddOnsForOrganization {
    getLinkedAddOnsForOrganization {
      ...PlanAddonSummaryFields
    }
  }
  ${PLAN_ADDON_SUMMARY_FIELDS}
`;

// Sprint 17, story-082 — SUPER_ADMIN only: link/unlink/reprice an add-on on a base plan.
export const linkAddonToPlan = gql`
  mutation linkAddonToPlan($planId: String!, $addonId: String!, $priceCents: Int!) {
    linkAddonToPlan(planId: $planId, addonId: $addonId, priceCents: $priceCents) {
      ...PlanAddonSummaryFields
    }
  }
  ${PLAN_ADDON_SUMMARY_FIELDS}
`;

export const unlinkAddonFromPlan = gql`
  mutation unlinkAddonFromPlan($planId: String!, $addonId: String!) {
    unlinkAddonFromPlan(planId: $planId, addonId: $addonId)
  }
`;

export const repriceAddonLink = gql`
  mutation repriceAddonLink($planId: String!, $addonId: String!, $priceCents: Int!) {
    repriceAddonLink(planId: $planId, addonId: $addonId, priceCents: $priceCents) {
      ...PlanAddonSummaryFields
    }
  }
  ${PLAN_ADDON_SUMMARY_FIELDS}
`;

// Get all subscription plan templates including add-ons (Admin)
export const getAllSubscriptionPlans = gql`
  query getAllSubscriptionPlans {
    getAllSubscriptionPlans {
      id
      name
      basePriceCents
      includedSeats
      isPublic
      isAddOn
      validFrom
      validTo
      createdAt
      updatedAt
    }
  }
`;

// Get subscription plan by ID
export const getSubscriptionPlanById = gql`
  query getSubscriptionPlanById($planId: String!) {
    getSubscriptionPlanById(planId: $planId) {
      id
      name
      basePriceCents
      includedSeats
      isPublic
      isAddOn
      trialDurationDays
      validFrom
      validTo
      createdAt
      updatedAt
    }
  }
`;

// LEGACY: Get pricing configuration (DEPRECATED - to be removed)
export const getPricingConfig = gql`
  query getPricingConfig {
    getPricingConfig {
      ...PricingConfigFields
    }
  }
  ${PRICING_CONFIG_FIELDS}
`;

// LEGACY: Get pricing configuration by key (DEPRECATED - to be removed)
export const getPricingConfigByKey = gql`
  query getPricingConfigByKey($key: String!) {
    getPricingConfigByKey(key: $key) {
      ...PricingConfigFields
    }
  }
  ${PRICING_CONFIG_FIELDS}
`;

// Assign plan to organization (SUPER_ADMIN only)
// This is an administrative operation that does NOT affect NMI billing
export const assignPlanToOrganization = gql`
  mutation assignPlanToOrganization(
    $organizationId: String!
    $planId: String!
    $billingPeriod: String
  ) {
    assignPlanToOrganization(
      organizationId: $organizationId
      planId: $planId
      billingPeriod: $billingPeriod
    ) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Grant or extend a bounded free period (SUPER_ADMIN only). Never contacts NMI.
export const extendOrganizationTrial = gql`
  mutation extendOrganizationTrial(
    $organizationId: String!
    $trialEndsAt: DateTime!
  ) {
    extendOrganizationTrial(
      organizationId: $organizationId
      trialEndsAt: $trialEndsAt
    ) {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

// Start subscription billing (create NMI subscription)
// Called by organization owners when they have an admin-assigned plan and want to start paying
export const startSubscription = gql`
  mutation startSubscription {
    startSubscription {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

export const SubscriptionGraphQL = {
  fragments: {
    ORGANIZATION_SUBSCRIPTION_FIELDS,
    PAYMENT_METHOD_FIELDS,
    TRANSACTION_FIELDS,
    PRICING_CONFIG_FIELDS,
    ACTIVE_SEAT_FIELDS,
    PLAN_ADDON_SUMMARY_FIELDS,
  },
  queries: {
    getOrganizationSubscription,
    getAddonSubscriptions,
    getTotalIncludedSeats,
    getSubscriptionAnalytics,
    getActiveSeats,
    getPaymentMethods,
    getTransactionHistory,
    getOrganizationTransactionHistory,
    getSubscriptionPlans,
    getAllSubscriptionPlans,
    getSubscriptionPlanById,
    getPricingConfig, // LEGACY - deprecated
    getPricingConfigByKey, // LEGACY - deprecated
    getFreeTrialOrganizationUsage,
    getLinkedAddOnsForOrganization,
  },
  mutations: {
    cancelOrganizationSubscription,
    pauseOrganizationSubscription,
    resumeOrganizationSubscription,
    addAddonSubscription,
    removeAddonSubscription,
    reactivateOrganizationSubscription,
    activateSubscriptionAfterTrial,
    addPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    setDefaultPaymentMethod,
    assignPlanToOrganization,
    extendOrganizationTrial,
    startSubscription,
    linkAddonToPlan,
    unlinkAddonFromPlan,
    repriceAddonLink,
  },
};
